import { ContextConsumer } from '@lit/context'
import { LitElement } from 'lit'
import { customElement } from 'lit/decorators.js'

import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { cardPartRegistryContext } from './card-context.js'
import style from './parts.css'

/**
 * The header region of a composed [Card](/components/card/). Lays a heading and
 * optional actions out in a row (heading at the start, actions at the end) and
 * draws the divider beneath the header.
 *
 * Place it as a direct child of `nord-card`, before
 * [Card Body](/components/card-body/). Direct child heading elements use the
 * same typography as the legacy Card header slot.
 *
 * @status draft
 * @category structure
 * @lightDom true
 *
 * @slot - Header content: a heading plus optional actions or controls.
 */
@customElement('nord-card-header')
export default class CardHeader extends DraftComponentMixin(LitElement) {
  private registry = new ContextConsumer(this, {
    context: cardPartRegistryContext,
    subscribe: true,
    callback: registry => registry?.add(this),
  })

  connectedCallback() {
    super.connectedCallback()
    this.registry.value?.add(this)
    injectLegacyLightDomStyles(this, style, 'nord-card-parts')
  }

  disconnectedCallback() {
    this.registry.value?.remove(this)
    super.disconnectedCallback()
  }

  protected createRenderRoot() {
    return this
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-card-header': CardHeader
  }
}
